import { MDSIconKind } from "@/mds/components/icon/icon/icon.types";
import type { OnboardingStepDefinition } from "./onboarding-types";

/**
 * The ordered onboarding step pipeline.
 *
 * Each step declares:
 *  - `applicableWhen` — whether this step should appear at all
 *  - `completedWhen` — whether the step has been completed/dismissed
 *  - `substeps` — declarative substeps with activeWhen predicates
 *  - `complete` — the action to run when the user completes or skips this step
 *
 * Steps with internal component navigation (voice-permissions → restart-if-required,
 * meeting-intro → meeting-permissions, import app screens) keep their
 * navigation inside the component. The substeps here only describe the
 * store-visible entry points.
 */
export const ONBOARDING_STEP_DEFINITIONS: OnboardingStepDefinition[] = [
  // 1. Native app download (web browsers on Mac/Win/iOS only)
  {
    id: "native-app-download",
    applicableWhen: ctx => ctx.shouldShowNativeAppDownload,
    completedWhen: ctx => ctx.hasCompletedOrSkippedNativeAppDownload,
    substeps: [{ id: "native-app-download", activeWhen: () => true }],
    complete: store => store.platformInfo.perDeviceOnboardingInfo.completeOrSkipNativeAppDownload(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetNativeAppDownload(),
    debugApplicableLabel: ctx => `shouldShowNativeAppDownload: ${ctx.shouldShowNativeAppDownload}`,
    debugCompletedLabel: ctx =>
      `hasCompletedOrSkippedNativeAppDownload: ${ctx.hasCompletedOrSkippedNativeAppDownload}`,
  },

  // 2. ICP RFI — user-profile + referrer substeps (always applicable)
  {
    id: "icp-rfi",
    applicableWhen: () => true,
    completedWhen: ctx => !ctx.shouldShowIcpRfi,
    substeps: [
      {
        id: "user-profile",
        activeWhen: ctx => !ctx.rfiCompletedSteps.includes("user-profile"),
        debugActiveLabel: ctx =>
          `rfiCompletedSteps includes "user-profile": ${ctx.rfiCompletedSteps.includes("user-profile")}`,
      },
      {
        id: "use-case",
        activeWhen: ctx =>
          ctx.rfiCompletedSteps.includes("user-profile") &&
          !ctx.rfiCompletedSteps.includes("use-case"),
        debugActiveLabel: ctx =>
          `rfiCompletedSteps includes "user-profile": ${ctx.rfiCompletedSteps.includes("user-profile")}, includes "use-case": ${ctx.rfiCompletedSteps.includes("use-case")}`,
      },
      {
        id: "referrer",
        activeWhen: ctx =>
          ctx.rfiCompletedSteps.includes("user-profile") &&
          ctx.rfiCompletedSteps.includes("use-case"),
        debugActiveLabel: ctx =>
          `rfiCompletedSteps includes "user-profile": ${ctx.rfiCompletedSteps.includes("user-profile")}, includes "use-case": ${ctx.rfiCompletedSteps.includes("use-case")}`,
      },
    ],
    complete: (store, completedSteps) =>
      store.platformInfo.onboardingInfo.completeIcpRfiStep(completedSteps ?? []),
    debugReset: store => store.platformInfo.onboardingInfo.debugReset(),
    debugApplicableLabel: () => "always applicable",
    debugCompletedLabel: ctx => `shouldShowIcpRfi: ${ctx.shouldShowIcpRfi}`,
  },

  // 3. Calendar setup — calendar-connect / calendar-select with declarative substep resolution (always applicable)
  {
    id: "calendar-setup",
    stepperIcon: MDSIconKind.Calendar,
    applicableWhen: () => true,
    completedWhen: ctx => !ctx.shouldShowCalendarOnboardingSetup,
    substeps: [
      {
        id: "calendar-connect",
        activeWhen: ctx => !ctx.hasPendingCalendarConnection,
        debugActiveLabel: ctx =>
          `hasPendingCalendarConnection: ${ctx.hasPendingCalendarConnection}`,
      },
      {
        id: "calendar-select",
        activeWhen: ctx => ctx.hasPendingCalendarConnection,
        debugActiveLabel: ctx =>
          `hasPendingCalendarConnection: ${ctx.hasPendingCalendarConnection}`,
      },
    ],
    complete: store => store.calendarOnboarding.setPhase("completed"),
    debugReset: store => store.calendarOnboarding.debugReset(),
    debugApplicableLabel: () => "always applicable",
    debugCompletedLabel: ctx =>
      `shouldShowCalendarOnboardingSetup: ${ctx.shouldShowCalendarOnboardingSetup}`,
  },

  // 4. Meeting permissions (Apple Silicon Mac desktop only) — intro is component-internal
  {
    id: "meeting-permissions",
    stepperIcon: MDSIconKind.Bell,
    applicableWhen: ctx => ctx.isAppleSiliconMacDesktop,
    completedWhen: ctx => !ctx.shouldShowMeetingOnboardingSystemPermissions,
    substeps: [{ id: "system-permissions-meeting-permissions", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.dismissMeetingOnboardingSystemPermissions(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetMeetingPermissions(),
    debugApplicableLabel: ctx => `isAppleSiliconMacDesktop: ${ctx.isAppleSiliconMacDesktop}`,
    debugCompletedLabel: ctx =>
      `shouldShowMeetingOnboardingSystemPermissions: ${ctx.shouldShowMeetingOnboardingSystemPermissions}`,
  },

  // 5. Voice permissions (Apple Silicon Mac desktop only) — restart-if-required is component-internal
  {
    id: "voice-permissions",
    stepperIcon: MDSIconKind.Microphone,
    applicableWhen: ctx => ctx.isAppleSiliconMacDesktop,
    completedWhen: ctx => !ctx.shouldShowVoiceOnboardingSystemPermissions,
    substeps: [{ id: "system-permissions-voice-permissions", activeWhen: () => true }],
    complete: store =>
      store.platformInfo.perDeviceOnboardingInfo.dismissVoiceOnboardingSystemPermissions(),
    debugReset: store => store.platformInfo.perDeviceOnboardingInfo.debugResetVoicePermissions(),
    debugApplicableLabel: ctx => `isAppleSiliconMacDesktop: ${ctx.isAppleSiliconMacDesktop}`,
    debugCompletedLabel: ctx =>
      `shouldShowVoiceOnboardingSystemPermissions: ${ctx.shouldShowVoiceOnboardingSystemPermissions}`,
  },
];
